import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { PcDialog } from 'app/shared/dialog/dialog';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InstanceService } from 'app/core/services/instance/instance.service';
import { StorageService } from 'app/core/services/storage/storage.service';
import { InvitationService } from 'app/core/services/invitation/invitation.service';
import { SendInvitationComponent } from 'app/shared/organisms/send-invitation/send-invitation';
import { Button } from 'app/shared/atoms/button/button';
import { InputDateRange } from 'app/shared/atoms/input-date-range/input-date-range';
import { MetricCard } from 'app/shared/atoms/metric-card/metric-card';
import { SvgIcon } from 'app/shared/svg-icon/svg-icon';
import { Modal } from 'app/shared/molecules/modal/modal';
import { NativeModalService } from 'app/shared/services/native-modal.service';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { EmptyDataParticules } from 'app/shared/molecules/empty-data-particules/empty-data-particules';
import { asDateTime } from 'app/shared/utils/luxon-date';
import { InstanceModal } from 'app/shared/organisms/instance-modal/instance-modal';
import { CommonModule } from '@angular/common';
import { Permissions } from 'app/shared/permissions/permissions';
import { HasPermissionDirective } from 'app/shared/directives/has-permission.directive';
import { GreetingService } from 'app/shared/greeting/greeting.service';
import { FlagChip } from 'app/shared/atoms/flag-chip/flag-chip';
import { DashboardService } from 'app/core/services/dashboard/dashboard.service';
import { SalesSummary } from 'app/core/models/sales-summary.model';
import { FunnelCard } from 'app/home/components/funnel-card/funnel-card';
import { LostCard } from 'app/home/components/lost-card/lost-card';
import { ClosedMonthsCard } from 'app/home/components/closed-months-card/closed-months-card';
import {
  DEFAULT_DASHBOARD_PERIOD,
  dashboardPeriodRange,
  previousYearRange,
} from 'app/home/dashboard-period';

@Component({
  selector: 'primecontrol-home',
  imports: [
    MetricCard,
    FunnelCard,
    LostCard,
    ClosedMonthsCard,
    Button,
    SvgIcon,
    RouterModule,
    HasPermissionDirective,
    TranslateModule,
    CommonModule,
    FlagChip,
    EmptyDataParticules,
  ],
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class Home implements OnInit {
  instance: WritableSignal<any> = signal({});
  private _modal: NativeModalService = inject(NativeModalService);
  private _router: Router = inject(Router);

  private _instanceService: InstanceService = inject(InstanceService);
  private _storageService: StorageService = inject(StorageService);
  private _translateService: TranslateService = inject(TranslateService);
  private _greetingService: GreetingService = inject(GreetingService);
  public permissions = Permissions;

  /** Hero content: time-of-day greeting, user first name and today's date. */
  public greeting = '';
  public greetingEmoji = '';
  /** Country whose flag chip goes beside the greeting, or null on a normal day. */
  public greetingFlag: string | null = null;
  public isNationalGreeting = false;
  public userFirstName = '';
  public todayLabel = signal('');

  /** Skeleton placeholders shown while the analytics response is in flight. */
  public skeletonItems = Array.from({ length: 8 }, (_, index) => index);

  /** Data freshness: when the last analytics response landed, and a 30s tick
   *  so the "updated X min ago" label stays honest without reloading. */
  public lastUpdatedAt = signal<number | null>(null);
  public refreshing = signal(false);
  private _nowTick = signal(Date.now());

  public updatedAgo = computed<{ key: string; params?: Record<string, number> } | null>(() => {
    const at = this.lastUpdatedAt();
    if (!at) return null;
    const minutes = Math.floor((this._nowTick() - at) / 60000);
    if (minutes < 1) return { key: 'UPDATED_JUST_NOW' };
    if (minutes < 60) return { key: 'UPDATED_MINUTES_AGO', params: { min: minutes } };
    return { key: 'UPDATED_HOURS_AGO', params: { hours: Math.floor(minutes / 60) } };
  });

  /** Workspace logo from the per-instance branding preference (same storage
   *  the layout header reads); the initials chip is the fallback. */
  public workspaceLogo = signal<string | null>(null);

  private _dashboardService: DashboardService = inject(DashboardService);

  /** Home sales dashboard payload; null until it lands, and on error. */
  public salesSummary = signal<SalesSummary | null>(null);
  public salesSummaryLoading = signal(true);
  /** The sales summary request failed: the cards say so instead of "no data". */
  public salesSummaryError = signal(false);
  private _summarySubscription: Subscription | null = null;
  private _previousSummarySubscription: Subscription | null = null;

  /** The analytics request failed: the tiles show an error, not skeletons. */
  public analyticsError = signal(false);

  /** The same window a year earlier, for the closed-months comparison. Null
   *  while it is in flight, and whenever there is nothing to compare against. */
  public salesSummaryPrevious = signal<SalesSummary | null>(null);

  /**
   * Period the sales summary is requested for, set by the control on the card
   * that heads the block.
   *
   * It lives here and not in a card because one payload feeds all three of them
   * — the closed months, the funnel and where deals are lost — so the window has
   * to be the page's, whichever card happens to host the control. A signal so
   * a pick re-requests the summary; the cards then name the window off the
   * payload they get back, not off this value.
   */
  private _salesSummaryPeriod = signal(DEFAULT_DASHBOARD_PERIOD);

  /** Re-requests the summary for the newly picked window. */
  public onSalesSummaryPeriodChange(period: string): void {
    if (period === this._salesSummaryPeriod()) {
      return;
    }
    this._salesSummaryPeriod.set(period);
    this._loadSalesSummary();
  }

  constructor() {
    try {
      const preference =
        sessionStorage.getItem('primecontrol_preference') ??
        localStorage.getItem('primecontrol_preference');
      if (preference) {
        const { square_logo, horizontal_logo } = JSON.parse(preference);
        this.workspaceLogo.set(square_logo || horizontal_logo || null);
      }
    } catch {
      // Corrupt/absent preference — keep the initials fallback.
    }

    effect(() => {
      // Default to {} — the service signal is null until the analytics
      // response lands (and on error), and the template renders before that.
      this.instance.set(this._instanceService.instance() ?? {});
    });

    this._buildHero();

    // The initial use() resolves after the catalog loads, and the user can
    // switch languages at runtime — recompute the date label on both.
    const langSub = this._translateService.onLangChange.subscribe((event) =>
      this._setTodayLabel(event.lang)
    );

    const tick = setInterval(() => this._nowTick.set(Date.now()), 30_000);
    inject(DestroyRef).onDestroy(() => {
      clearInterval(tick);
      langSub.unsubscribe();
    });
  }

  /** Resolve the greeting, capitalized first name and localized date eyebrow. */
  private _buildHero(): void {
    let storedUser: any = null;
    try {
      storedUser = JSON.parse(this._storageService.getItem('primecontrol_user'));
    } catch {
      storedUser = null;
    }
    const firstName: string = storedUser?.firstName ?? '';
    this.userFirstName = firstName
      ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
      : '';

    const result = this._greetingService.resolveGreeting({ trackReturnVisit: true });
    this.greeting = result.text;
    this.greetingEmoji = result.emoji;
    this.greetingFlag = result.flag;
    this.isNationalGreeting = result.isNationalDate;

    const lang = this._translateService.currentLang || this._translateService.defaultLang || 'es';
    this._setTodayLabel(lang);
  }

  /** Format today's date in the given language (e.g. "jueves, 9 de julio"). */
  private _setTodayLabel(lang: string): void {
    this.todayLabel.set(
      new Intl.DateTimeFormat(lang, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }).format(new Date())
    );
  }

  ngOnInit(): void {
    // Loaded here instead of a route resolver so the page shell paints
    // immediately; the metric cards fill in when the response lands.
    this._loadAnalytics();
  }

  /** Re-fetch the dashboard analytics on demand. */
  refresh(): void {
    this._loadAnalytics();
  }

  /** RouterLink on a non-anchor host only reacts to clicks; mirror Enter and
   *  Space (what role="link" users expect) to a click so keyboard users can
   *  open the metric they focused. preventDefault keeps Space from scrolling. */
  onMetricEnter(event: Event): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).click();
  }

  private _loadAnalytics(): void {
    this.refreshing.set(true);
    this.salesSummaryLoading.set(true);

    this.analyticsError.set(false);
    this._instanceService.instanceAnalytics().subscribe({
      next: () => {
        this.lastUpdatedAt.set(Date.now());
        this._nowTick.set(Date.now());
        this.refreshing.set(false);
      },
      // No "updated" stamp for a failed load, and the tiles say so instead of
      // spinning forever.
      error: () => {
        this.analyticsError.set(true);
        this.refreshing.set(false);
      },
    });

    // Separate call, separate permission (Offers.View): a user may hold one and
    // not the other, so a 403 here must not blank the metric tiles.
    this._loadSalesSummary();
  }

  /**
   * Fetches the sales summary for the current period, plus the same window a
   * year earlier as the comparison baseline.
   *
   * Split out of `_loadAnalytics` so changing the period reloads only this
   * payload and leaves the instance metrics alone.
   *
   * The two requests are independent on purpose: the loading flag and the cards
   * hang off the current window only, so a comparison that is slow, forbidden or
   * simply empty never delays or blanks the chart — the ghost bars just do not
   * appear.
   */
  private _loadSalesSummary(): void {
    const range = dashboardPeriodRange(this._salesSummaryPeriod());

    // A newer pick cancels the requests still in flight: otherwise a slow
    // 12-month response landing after the 3-month one showed 12 months of data
    // under a "3 months" control.
    this._summarySubscription?.unsubscribe();
    this._previousSummarySubscription?.unsubscribe();

    this.salesSummaryLoading.set(true);
    this.salesSummaryError.set(false);
    this._summarySubscription = this._dashboardService.getSalesSummary(range).subscribe({
      next: (summary) => {
        this.salesSummary.set(summary);
        this.salesSummaryLoading.set(false);
      },
      // Drop the previous window's figures: keeping them showed the old period
      // under the new selection, and an empty payload read as "nothing lost".
      error: () => {
        this.salesSummary.set(null);
        this.salesSummaryError.set(true);
        this.salesSummaryLoading.set(false);
      },
    });

    this.salesSummaryPrevious.set(null);
    this._previousSummarySubscription = this._dashboardService
      .getSalesSummary(previousYearRange(range))
      .subscribe({
        next: (summary) => this.salesSummaryPrevious.set(summary),
        // The comparison is optional: without it the ghost bars just don't draw.
        error: () => this.salesSummaryPrevious.set(null),
      });
  }

  getInitials(): string {
    const text = this.instance()?.name;
    if (!text) return '';

    // Dividir por uno o más espacios para evitar errores con dobles espacios
    const parts = text.trim().split(/\s+/);

    if (parts.length === 1) {
      // Si es una sola palabra, devolver las 2 primeras letras
      return parts[0].substring(0, 2).toUpperCase();
    }

    // Si son 2 o más palabras, devolver la primera letra de la 1ra y 2da palabra
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  /**
   * Opens send invitation modal using NativeModalService
   * API is similar to PcDialog but optimized for iOS touch performance
   */
  openSendInvitation(): void {
    this._modal
      .open({
        component: SendInvitationComponent,
        data: {
          id: this.instance().id,
        },
      })
      .afterClosed()
      .subscribe();
  }

  /**
   * Alternative: Open with PcDialog (for comparison)
   */
  // openSendInvitationMatDialog(): void {
  //   this._matDialog.open(Modal, {
  //     autoFocus: false,
  //     data: {
  //       component: SendInvitationComponent,
  //       data: {
  //         id: this.instance().id,
  //       },
  //     },
  //   });
  // }

  /** Every metric chip carries the product's sapphire accent (the CTA color)
   *  so the dashboard reads as one system instead of a per-module rainbow.
   *  computed (not a getter): a getter would mint new objects on every change
   *  detection cycle, retriggering the cards' count-up on unrelated events
   *  like hovering the sidebar. */
  readonly analyticsWithColors = computed<any[]>(() =>
    (this.instance()?.analytics || []).map((metric: any) => ({
      ...metric,
      color: 'sapphire',
    }))
  );

  // openInstance() {
  //   this._matDialog.open(Modal, {
  //     autoFocus: false,
  //     data: {
  //       component: InstanceModal,
  //       data: {
  //         instance: this.instance(),
  //       },
  //     },
  //   });
  // }

  openInstance(): void {
    const id = this.instance()?.id;
    if (!id) return;
    this._router.navigate(['/settings/instances', id]);
  }
}
